<template>
    <div class="qi-page">
        <div class="qi-page-header d-flex justify-content-between align-items-start flex-wrap">
            <div>
                <a href="/invoices" class="btn btn-sm btn-outline-secondary mb-2"><i class="bi bi-arrow-left"></i> Back</a>
                <h4 class="mb-0 text-primary">{{ isEdit ? 'Edit Invoice' : 'New Invoice' }}</h4>
            </div>
            <div class="qi-header-actions" style="gap:8px;">
                <button type="button" class="btn btn-outline-primary rounded" :disabled="!items.length" @click="showPreview = true">Preview</button>
                <button type="button" class="btn btn-primary rounded" :disabled="saving || !items.length" @click="save">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
                    Save Invoice
                </button>
            </div>
        </div>

        <div v-if="loading" class="text-center py-5">
            <span class="spinner-border"></span>
        </div>

        <div v-else class="qi-form-card">
            <div class="row">
                <div class="col-md-6">
                    <customer-picker v-model="customer"></customer-picker>
                </div>
                <div class="col-md-6">
                    <div class="form-group row align-items-center">
                        <label class="col-12 col-sm-5 col-form-label text-sm-right small">Invoice number</label>
                        <div class="col-12 col-sm-7">
                            <input type="text" class="form-control" v-model="form.number" placeholder="Generating…">
                        </div>
                    </div>
                    <div class="form-group row align-items-center">
                        <label class="col-12 col-sm-5 col-form-label text-sm-right small">P.O./S.O. number</label>
                        <div class="col-12 col-sm-7">
                            <input type="text" class="form-control" v-model="form.po_number" placeholder="Optional">
                        </div>
                    </div>
                    <div class="form-group row align-items-center">
                        <label class="col-12 col-sm-5 col-form-label text-sm-right small">Invoice date</label>
                        <div class="col-12 col-sm-7">
                            <date-range-picker :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" v-model="datePicker" class="custom-width-datepicker">
                                <template #input="picker">
                                    <span :class="picker.startDate ? 'text-dark' : 'text-muted'">{{ picker.startDate ? formatPickerDate(picker.startDate) : 'Select date' }}</span>
                                    <i class="bi bi-calendar-event qi-date-icon"></i>
                                </template>
                            </date-range-picker>
                        </div>
                    </div>
                    <div class="form-group row align-items-start">
                        <label class="col-12 col-sm-5 col-form-label text-sm-right small">Payment due</label>
                        <div class="col-12 col-sm-7">
                            <date-range-picker :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" v-model="dueDatePicker" class="custom-width-datepicker">
                                <template #input="picker">
                                    <span :class="picker.startDate ? 'text-dark' : 'text-muted'">{{ picker.startDate ? formatPickerDate(picker.startDate) : 'Select date' }}</span>
                                    <i class="bi bi-calendar-event qi-date-icon"></i>
                                </template>
                            </date-range-picker>
                            <small v-if="dueDaysFromNow !== null" class="text-muted d-block mt-1">Within {{ dueDaysFromNow }} days</small>
                        </div>
                    </div>
                </div>
            </div>

            <hr>

            <line-items-editor ref="lineItemsEditor" v-model="items" :currency="currency"></line-items-editor>

            <hr>

            <div class="qi-totals-box qi-totals-box-standalone">
                <div class="d-flex justify-content-between"><span>Subtotal</span><span>{{ formatMoney(preview.subtotal) }}</span></div>

                <div v-if="!discountEditor.active" class="qi-add-discount-row">
                    <button type="button" class="btn p-0 qi-discount-link" @click="openDiscountEditor">
                        <i class="bi bi-plus-circle mr-1"></i>Add a discount
                    </button>
                </div>
                <div v-else class="qi-discount-row">
                    <span class="qi-discount-row-label text-muted">Discount</span>
                    <input type="number" min="0" step="0.01" class="form-control qi-discount-amount" v-model="discountEditor.value" placeholder="0" @input="applyDiscount">
                    <select class="custom-select qi-discount-unit" v-model="discountEditor.type" @change="applyDiscount">
                        <option value="amount">{{ currency }}</option>
                        <option value="percent">%</option>
                    </select>
                    <span class="qi-discount-row-computed">-{{ formatMoney(preview.discount) }}</span>
                    <button type="button" class="btn btn-sm btn-link text-danger p-0" title="Remove discount" @click="removeDiscount"><i class="bi bi-trash"></i></button>
                </div>

                <div class="d-flex justify-content-between align-items-center text-muted">
                    <span>Tax</span>
                    <span>
                        {{ formatMoney(preview.tax) }}
                        <button type="button" class="btn p-0 ml-2 qi-discount-link" title="Create a new tax / levy" @click="showTaxModal = true">
                            <i class="bi bi-plus-circle"></i>
                        </button>
                    </span>
                </div>
                <hr class="my-2">
                <div class="d-flex justify-content-between font-weight-bold h6"><span>Total</span><span>{{ formatMoney(preview.total) }}</span></div>
                <small class="text-muted">Final totals are confirmed by the server on save. Record payment after saving.</small>
            </div>

            <tax-quick-add-modal v-if="showTaxModal" @close="showTaxModal = false" @created="onTaxCreated"></tax-quick-add-modal>

            <div class="qi-notes-card">
                <label class="qi-notes-title mb-2">Notes / Terms</label>
                <div class="form-group mb-2">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <label class="small text-muted mb-0">Notes <span class="text-muted">(visible to customer)</span></label>
                        <button v-if="notesPreset && form.notes !== notesPreset" type="button" class="btn btn-sm btn-link p-0" @click="form.notes = notesPreset">Use preset</button>
                    </div>
                    <textarea class="form-control" rows="2" v-model="form.notes" placeholder="Enter notes visible to your customer"></textarea>
                </div>
                <div class="form-group mb-0">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <label class="small text-muted mb-0">Terms</label>
                        <button v-if="termsPreset && form.terms !== termsPreset" type="button" class="btn btn-sm btn-link p-0" @click="form.terms = termsPreset">Use preset</button>
                    </div>
                    <textarea class="form-control" rows="2" v-model="form.terms" placeholder="Enter terms of service, e.g. payment due within 14 days"></textarea>
                </div>
            </div>

            <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>

            <div class="d-flex justify-content-end mt-3" style="gap:8px;">
                <a href="/invoices" class="btn btn-outline-secondary rounded">Cancel</a>
                <button type="button" class="btn btn-outline-primary rounded" :disabled="!items.length" @click="showPreview = true">Preview</button>
                <button type="button" class="btn btn-primary rounded" :disabled="saving || !items.length" @click="save">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
                    Save Invoice
                </button>
            </div>
        </div>

        <document-preview-modal
            v-if="showPreview"
            doc-type="Invoice"
            :customer="customer"
            :items="items"
            :notes="form.notes"
            :terms="form.terms"
            :date="form.date"
            :expiry-date="form.due_date"
            :totals="preview"
            :currency="currency"
            @close="showPreview = false"
        ></document-preview-modal>
    </div>
</template>

<script>
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import CustomerPicker from './CustomerPicker.vue';
import LineItemsEditor from './LineItemsEditor.vue';
import DocumentPreviewModal from './DocumentPreviewModal.vue';
import TaxQuickAddModal from './TaxQuickAddModal.vue';
import { formatMoney, apiErrorMessage } from './docHelpers';

export default {
    name: 'InvoiceForm',
    components: { CustomerPicker, LineItemsEditor, DocumentPreviewModal, DateRangePicker, TaxQuickAddModal },
    props: {
        invoiceId: { type: String, default: '' },
    },
    data() {
        return {
            loading: false,
            saving: false,
            showPreview: false,
            error: '',
            customer: null,
            items: [],
            form: {
                number: '',
                po_number: '',
                date: new Date().toISOString().slice(0, 10),
                due_date: '',
                notes: '',
                terms: '',
            },
            notesPreset: '',
            termsPreset: '',
            discountEditor: { active: false, type: 'amount', value: '' },
            showTaxModal: false,
        };
    },
    computed: {
        isEdit() { return !!this.invoiceId; },
        dueDaysFromNow() {
            if (!this.form.date || !this.form.due_date) return null;
            const days = Math.round((new Date(this.form.due_date) - new Date(this.form.date)) / 86400000);
            return days > 0 ? days : null;
        },
        datePicker: {
            get() { return { startDate: this.form.date ? new Date(this.form.date) : null }; },
            set(val) { this.form.date = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : ''; },
        },
        dueDatePicker: {
            get() { return { startDate: this.form.due_date ? new Date(this.form.due_date) : null }; },
            set(val) { this.form.due_date = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : ''; },
        },
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
        preview() {
            let subtotal = 0, discount = 0, tax = 0;
            this.items.forEach(item => {
                const lineBase = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
                const lineDiscount = Number(item.discount) || 0;
                const taxable = Math.max(0, lineBase - lineDiscount);
                const taxPerc = item.tax ? Number(item.tax.tax_perc) : 0;
                subtotal += lineBase;
                discount += lineDiscount;
                tax += taxPerc ? taxable * (taxPerc / 100) : 0;
            });
            return { subtotal, discount, tax, total: Math.max(0, subtotal - discount) + tax };
        },
    },
    created() {
        if (this.isEdit) {
            this.loadInvoice();
        } else {
            const cs = (this.$store.getters.userDetails.company || {}).companysettings || {};
            this.notesPreset = cs.invoice_default_notes || '';
            this.termsPreset = cs.invoice_default_terms || '';
            this.form.notes = this.notesPreset;
            this.form.terms = this.termsPreset;
            if (cs.invoice_due_days) {
                const d = new Date();
                d.setDate(d.getDate() + Number(cs.invoice_due_days));
                this.form.due_date = d.toISOString().slice(0, 10);
            }
            this.loadNextNumber();
        }
    },
    methods: {
        formatMoney(v) { return formatMoney(v, this.currency); },
        // Pre-fills the number a new invoice would get so the merchant sees
        // it immediately and can edit it before saving if they want a
        // different one - the server still enforces uniqueness on save.
        async loadNextNumber() {
            try {
                const { data } = await axios.get('/api/invoices/next-number');
                this.form.number = data.number || '';
            } catch (e) {
                // Leave blank - the server will generate one on save if this
                // field ends up empty.
            }
        },
        formatPickerDate(d) {
            if (!d) return '';
            return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        },
        openDiscountEditor() {
            this.discountEditor.type = 'amount';
            this.discountEditor.value = this.preview.discount > 0 ? String(this.preview.discount) : '';
            this.discountEditor.active = true;
        },
        // A document-wide discount isn't its own field on the Invoice model -
        // the server only stores/sums per-line discounts (see
        // DocumentTotalsCalculator). So this spreads the entered amount
        // across the current line items, proportional to each line's share
        // of the subtotal, re-running live on every edit while the discount
        // row is open rather than requiring a separate "Apply" step.
        applyDiscount() {
            const grossSubtotal = this.items.reduce((sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
            if (!this.items.length || grossSubtotal <= 0) {
                return;
            }
            const raw = Number(this.discountEditor.value) || 0;
            const requested = this.discountEditor.type === 'percent' ? grossSubtotal * (raw / 100) : raw;
            const totalDiscount = Math.max(0, Math.min(requested, grossSubtotal));

            let remaining = totalDiscount;
            this.items.forEach((item, idx) => {
                const lineGross = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
                const isLast = idx === this.items.length - 1;
                const share = isLast ? remaining : Math.round(totalDiscount * (lineGross / grossSubtotal) * 100) / 100;
                item.discount = share;
                remaining = Math.round((remaining - share) * 100) / 100;
            });
        },
        removeDiscount() {
            this.items.forEach(item => { item.discount = 0; });
            this.discountEditor.active = false;
            this.discountEditor.value = '';
        },
        // The tax picker per line lives inside LineItemsEditor (it fetches
        // its own /api/taxes list), so a tax created from this totals-box
        // shortcut needs to be pushed into that same list to show up there
        // immediately - it isn't applied to any line automatically.
        onTaxCreated(tax) {
            this.showTaxModal = false;
            const editor = this.$refs.lineItemsEditor;
            if (editor && Array.isArray(editor.taxes) && !editor.taxes.some(t => t.id === tax.id)) {
                editor.taxes.push(tax);
            }
        },
        async loadInvoice() {
            this.loading = true;
            const cs = (this.$store.getters.userDetails.company || {}).companysettings || {};
            this.notesPreset = cs.invoice_default_notes || '';
            this.termsPreset = cs.invoice_default_terms || '';
            try {
                const { data } = await axios.get(`/api/invoices/${this.invoiceId}`);
                this.customer = data.customer || null;
                this.form.number = data.number || '';
                this.form.po_number = data.po_number || '';
                this.form.date = data.date ? data.date.slice(0, 10) : this.form.date;
                this.form.due_date = data.due_date ? data.due_date.slice(0, 10) : '';
                this.form.notes = data.notes || '';
                this.form.terms = data.terms || '';
                this.items = (data.items || []).map(i => ({
                    _key: 'li_' + i.id,
                    product_id: i.product_id,
                    description: i.description,
                    item_note: i.item_note || '',
                    quantity: Number(i.quantity),
                    unit_price: Number(i.unit_price),
                    discount: Number(i.discount || 0),
                    tax_id: i.tax_id,
                    tax: i.tax || null,
                }));
                // Reopen the discount row on edit if this invoice already has
                // per-line discounts applied, instead of hiding them behind
                // the "+ Add a discount" link.
                if (Number(data.discount) > 0) {
                    this.discountEditor.active = true;
                    this.discountEditor.value = String(data.discount);
                }
            } catch (e) {
                this.error = apiErrorMessage(e, 'Could not load this invoice.');
            } finally {
                this.loading = false;
            }
        },
        async save() {
            this.error = '';
            if (!this.items.length) return;
            this.saving = true;
            const payload = {
                customer_id: this.customer ? this.customer.id : null,
                number: this.form.number || null,
                po_number: this.form.po_number || null,
                date: this.form.date || null,
                due_date: this.form.due_date || null,
                notes: this.form.notes || null,
                terms: this.form.terms || null,
                items: this.items.map(i => ({
                    product_id: i.product_id || null,
                    description: i.description,
                    item_note: i.item_note || null,
                    quantity: i.quantity,
                    unit_price: i.unit_price,
                    discount: i.discount || 0,
                    tax_id: i.tax_id || null,
                })),
            };
            try {
                const { data } = this.isEdit
                    ? await axios.put(`/api/invoices/${this.invoiceId}`, payload)
                    : await axios.post('/api/invoices', payload);
                this.$toasts.push({ type: 'success', message: this.isEdit ? 'Invoice updated.' : 'Invoice created.' });
                window.location.href = `/invoices/${data.id}`;
            } catch (e) {
                this.error = apiErrorMessage(e, 'Could not save this invoice. Please check the form and try again.');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
.qi-page { padding: 16px; max-width: 1100px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 130px; } }
.qi-page-header { margin-bottom: 12px; }
.qi-header-actions { display: flex; }
.qi-form-card { background: #fff; border: 1px solid #eee; border-radius: 10px; padding: 20px; }
.qi-totals-box { background: #f8f9fa; border-radius: 8px; padding: 18px 22px; }
.qi-totals-box > .d-flex { padding: 4px 0; }
.qi-totals-box-standalone { max-width: 440px; margin: 18px 0 0 auto; }
.qi-notes-card { border: 1px solid #eee; border-radius: 10px; padding: 16px; margin-top: 18px; }
.qi-notes-title { font-size: .78rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d; font-weight: 600; display: block; }

.qi-form-card .form-control {
    border-radius: 8px;
    border-color: #d7dde3;
}
.qi-form-card .form-control:focus {
    border-color: #b8c0c8;
    box-shadow: 0 0 0 .15rem rgba(33,37,41,.08);
}

.qi-date-icon { float: right; color: #8a97a8; margin-top: 2px; }
::v-deep .custom-width-datepicker .reportrange-text {
    border-radius: 8px !important;
    border-color: #d7dde3 !important;
    overflow: hidden;
}

.qi-add-discount-row { margin: 6px 0; }
.qi-discount-link { color: #fd4719; }
.qi-discount-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin: 8px 0;
}
.qi-discount-row-label { flex-shrink: 0; }
.qi-discount-amount { width: 120px; flex-shrink: 0; }
.qi-discount-unit { width: 90px; flex-shrink: 0; }
.qi-discount-row-computed { flex: 1 1 auto; min-width: 80px; text-align: right; color: #6c757d; }
</style>
